"use client"

// components/auctions/global-auction-bid-sheet.tsx
//
// Overlay-FSM utility-slot consumer (migrated step 1.4, spec §E) — was a
// `window` CustomEvent listener (`lib/auction-quick-bid-sheet-event.ts`,
// deleted this step). Visibility = `useUtilityIs("auction-bid")`; the open
// listing id + prefetched description come straight off `overlay.utility`
// (the FSM state itself, not a side-channel). Fetched auction detail
// (`payload`/`loading`/`error`) stays component-internal per §C.1's
// inclusion criterion — only open/closed is FSM-owned, same as cart's
// internal `ViewState` (step 1.3).
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §E row 1.4

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createClient } from "@/utils/supabase/client"
import { Clock, Loader2 } from "lucide-react"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { useMobile } from "@/hooks/use-mobile"
import {
  AuctionPanel,
  auctionDataToInitial,
  type AuctionData,
} from "@/components/detailed-listing-view/auction-panel"
import { useTranslations } from "next-intl"
import { useOverlay, useUtilityIs } from "@/components/context/overlay-context"
import { Button } from "@/components/ui/button"
import { useAuction } from "@/hooks/use-auction"
import { formatAuctionCountdown } from "@/lib/format-auction-countdown"
import { serverNow } from "@/lib/clock-sync"
import { SHEET_Z_GLOBAL } from "@/lib/sheet-styles"

type SheetPayload = {
  title: string
  description: string | null
  images: string[]
  currency: string | null
  sellerId: string
  auction: AuctionData
  auctionCountryBanWarning: string | null
}

function normalizeSheetDescription(raw: unknown): string | null {
  if (raw == null) return null
  if (typeof raw === "string") {
    const t = raw.trim()
    return t.length > 0 ? t : null
  }
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw)
  return null
}

function QuickBidCountdownLine({
  listingId,
  initialAuction,
}: {
  listingId: string
  initialAuction: AuctionData
}) {
  const t = useTranslations("auction")
  const slot = useAuction(listingId, { initial: auctionDataToInitial(initialAuction), broadcast: true })
  const endAt = slot?.endAt ?? initialAuction.endAt
  const labels = useMemo(
    () => ({
      d: t("countdown.d"),
      h: t("countdown.h"),
      m: t("countdown.m"),
      s: t("countdown.s"),
    }),
    [t],
  )
  const [totalSeconds, setTotalSeconds] = useState(0)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!endAt) {
      setTotalSeconds(0)
      return
    }
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(endAt).getTime() - serverNow()) / 1000))
      setTotalSeconds(diff)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [endAt])

  if (!mounted || !endAt || totalSeconds <= 0) return null

  return (
    <div className="flex items-center gap-1.5 text-amber-400">
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="text-sm font-bold tabular-nums tracking-tight">
        {formatAuctionCountdown(totalSeconds, labels)}
      </span>
    </div>
  )
}

export function GlobalAuctionBidSheet() {
  const isMobile = useMobile()
  const t = useTranslations("auction")
  const tCommon = useTranslations("common")

  // Overlay-driven visibility + payload (migrated 2026-07-23, spec §E step
  // 1.4 — was local `listingId` useState + a `window` CustomEvent listener
  // on `lib/auction-quick-bid-sheet-event.ts`, deleted this step). The
  // utility slot IS this sheet's open/closed state; `listingId`/
  // `description` come straight off `overlay.utility` — no side-channel.
  const open = useUtilityIs("auction-bid")
  const { overlay, closeUtility } = useOverlay()
  const utility = overlay.utility.kind === "auction-bid" ? overlay.utility : null
  const listingId = utility?.listingId ?? null

  const [userId, setUserId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [payload, setPayload] = useState<SheetPayload | null>(null)
  const [quickBidFooterHost, setQuickBidFooterHost] = useState<HTMLDivElement | null>(null)
  const [quickBidFooterSlotActive, setQuickBidFooterSlotActive] = useState(false)
  const prefetchedDescriptionRef = useRef<string | null>(null)
  const quickBidFooterMountRef = useCallback((node: HTMLDivElement | null) => {
    setQuickBidFooterHost((prev) => (prev === node ? prev : node))
  }, [])
  const handleQuickBidFooterSlotActive = useCallback((active: boolean) => {
    setQuickBidFooterSlotActive(active)
  }, [])

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null))
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  // Open/re-open effect — mirrors the old CustomEvent handler: fires on
  // EVERY `openUtility({kind:"auction-bid",...})` dispatch, including a
  // re-click while the sheet is already open on the SAME listing
  // (`openUtility` always produces a fresh object per call — same identity
  // note as cart's step-1.3 open effect, components/cart/cart.tsx). Resets
  // `payload`/`error` and stashes the prefetched description every time,
  // matching pre-migration behavior exactly (deliberately keyed on the
  // `overlay.utility` object reference, not the derived `listingId`
  // primitive, so a same-id re-fire still resets — same-id re-fire did NOT
  // re-trigger the fetch effect below pre-migration either, since that one
  // depends only on the `listingId` primitive).
  useEffect(() => {
    const current = overlay.utility
    if (current.kind !== "auction-bid") return
    prefetchedDescriptionRef.current = normalizeSheetDescription(current.description ?? null)
    setPayload(null)
    setError(false)
  }, [overlay.utility])

  useEffect(() => {
    if (!listingId) return
    let cancelled = false
    setLoading(true)
    setError(false)
    fetch(`/api/listings/${encodeURIComponent(listingId)}/auction-quick-bid`, { cache: "no-store" })
      .then(async (res) => {
        const data = (await res.json().catch(() => null)) as
          | SheetPayload
          | ({ error?: string; code?: string; listingId?: string } & Record<string, unknown>)
          | null
        if (!res.ok || !data || "error" in data) {
          console.warn("[auction-quick-bid client]", listingId, res.status, data)
          if (!cancelled) {
            prefetchedDescriptionRef.current = null
            setError(true)
          }
          return
        }
        const row = data as SheetPayload
        const fromApi = normalizeSheetDescription(row.description)
        const fromPrefetch = prefetchedDescriptionRef.current
        prefetchedDescriptionRef.current = null
        const description = fromApi ?? fromPrefetch
        if (!cancelled) setPayload({ ...row, description })
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [listingId])

  // Close/cleanup effect — fires once on every true→false transition of
  // `open`, regardless of WHY it closed: the sheet's own close affordances
  // (X button, backdrop, error-state close button — all now just call
  // `closeUtility()`) or another overlay taking the utility slot out from
  // under it (§C.1 policy — e.g. cart opening). Mirrors cart's step-1.3
  // close/cleanup effect (components/cart/cart.tsx).
  const wasOpenRef = useRef(false)
  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open
    if (!wasOpen || open) return // only true -> false transitions
    prefetchedDescriptionRef.current = null
    setPayload(null)
    setError(false)
    setLoading(false)
  }, [open])

  const thumb = payload?.images?.[0]
  const descriptionText =
    payload?.description && typeof payload.description === "string" ? payload.description.trim() : ""

  const thumbHeroBlock =
    !loading && !error && payload ? (
      isMobile ? (
        <>
          <div className="flex items-start gap-3 py-3 shrink-0">
            {thumb ? (
              <img src={thumb} alt="" className="h-16 w-16 shrink-0 rounded-2xl object-cover bg-muted ring-1 ring-sheet-handle" />
            ) : (
              <div className="h-16 w-16 shrink-0 rounded-2xl bg-muted ring-1 ring-sheet-handle" />
            )}
            <div className="min-w-0 flex-1 flex flex-col gap-1.5 justify-center pt-0.5">
              <h3 className="text-[17px] font-semibold text-foreground truncate min-w-0">{payload.title}</h3>
              {descriptionText ? (
                <p className="text-[13px] text-muted-foreground leading-snug line-clamp-6 break-words">{descriptionText}</p>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 justify-start pb-2 -mt-1">
            <QuickBidCountdownLine listingId={listingId!} initialAuction={payload.auction} />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start gap-3 py-3 shrink-0">
            {thumb ? (
              <img src={thumb} alt="" className="h-16 w-16 shrink-0 rounded-2xl object-cover bg-muted ring-1 ring-sheet-handle" />
            ) : (
              <div className="h-16 w-16 shrink-0 rounded-2xl bg-muted ring-1 ring-sheet-handle" />
            )}
            <div className="min-w-0 flex-1 flex flex-col gap-1.5 justify-center pt-0.5">
              {descriptionText ? (
                <p className="text-[13px] text-muted-foreground leading-snug line-clamp-6 break-words">{descriptionText}</p>
              ) : (
                <QuickBidCountdownLine listingId={listingId!} initialAuction={payload.auction} />
              )}
            </div>
          </div>
          {descriptionText ? (
            <div className="flex shrink-0 justify-start pb-2 -mt-1">
              <QuickBidCountdownLine listingId={listingId!} initialAuction={payload.auction} />
            </div>
          ) : null}
        </>
      )
    ) : null

  const body = (
    <div className="flex min-h-0 flex-1 flex-col px-4 pt-2 pb-2">
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[280px] px-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : error ? (
        <div className="space-y-4 py-8 text-center pb-[calc(env(safe-area-inset-bottom)+12px)]">
          <p className="text-sm text-foreground/70">{t("quickBidLoadError")}</p>
          <Button type="button" variant="secondary" className="rounded-full bg-muted text-foreground hover:bg-sheet-handle" onClick={() => closeUtility()}>
            {tCommon("close")}
          </Button>
        </div>
      ) : payload ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {thumbHeroBlock}
          {payload.auctionCountryBanWarning ? (
            <div className="rounded-xl border border-amber-800/40 bg-amber-950/40 p-3 shrink-0">
              <p className="text-sm text-amber-100">{payload.auctionCountryBanWarning}</p>
            </div>
          ) : null}
          <div className="flex min-h-0 flex-1 flex-col">
            <AuctionPanel
              listingId={listingId!}
              auction={payload.auction}
              currency={payload.currency}
              currentUserId={userId}
              sellerId={payload.sellerId}
              showAuctionLiveChat={false}
              surfaceVariant="quickSheet"
              quickSheetFooterHost={quickBidFooterHost}
              onQuickSheetBidFooterSlotActiveChange={handleQuickBidFooterSlotActive}
              onRequestClose={closeUtility}
            />
          </div>
        </div>
      ) : null}
    </div>
  )

  if (!open) return null

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) closeUtility()
        }}
        // The body opens with the listing's own title block, so a chrome title
        // would repeat it — handle + close only.
        chrome="handleOnly"
        contentSized
        zIndex={SHEET_Z_GLOBAL}
        // The primitive owns the gradient and the safe-area padding; this slot
        // is just the mount point the quick-bid CTA portals into.
        footer={
          quickBidFooterSlotActive ? (
            <div ref={quickBidFooterMountRef} className="w-full min-h-14" />
          ) : undefined
        }
      >
        {body}
      </DockSheet>
    )
  }

  /* Two chromes on one surface (§8 finding 2): while the fetch is in flight
     there is no title to show, so the row is close-only — the desktop mirror
     of the sheet's `handleOnly` above. Once the payload lands (or the fetch
     fails) the listing title becomes the chrome title. Both variants paint
     the same `p-4` row with the same 36px control, so the body does not jump
     when the spinner resolves.

     The footer is the SAME portal host the sheet branch passes: `AuctionPanel`
     mounts the quick-bid CTA into it, and the primitive owns the gradient and
     the safe-area padding that used to be rebuilt here by hand. */
  const desktopFooter = quickBidFooterSlotActive ? (
    <div ref={quickBidFooterMountRef} className="w-full min-h-14" />
  ) : undefined

  const panelClassName = "max-h-[90vh] min-h-[min(26rem,85dvh)] max-w-md shadow-2xl"

  if (loading) {
    return (
      <DialogCard
        open={open}
        onOpenChange={(o) => !o && closeUtility()}
        chrome="closeOnly"
        a11yTitle={t("quickBidTitle")}
        panelClassName={panelClassName}
        contentClassName="px-0 pt-0 scrollbar-hide"
        footer={desktopFooter}
      >
        {body}
      </DialogCard>
    )
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(o) => !o && closeUtility()}
      title={error ? t("quickBidTitle") : payload?.title ?? t("quickBidTitle")}
      titleAlign="left"
      panelClassName={panelClassName}
      contentClassName="px-0 pt-0 scrollbar-hide"
      footer={desktopFooter}
    >
      {body}
    </DialogCard>
  )
}
